import React, { useState, useEffect, useMemo } from 'react';
import { Download, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import NowName, { staffNow } from './shared/NowName';
import { exportToExcel } from '../utils/excelExport';
import { formatDate as toDisplayDate, formatDateShort } from '../utils/dateFormat';
import DashboardTabHeader from './shared/DashboardTabHeader';
import { PAGE_SIZE, TablePager } from './SortableTable';
import { exportExcelBtnCls } from './shared/ExportExcelButton';

const parseDMY = (str) => {
  if (!str) return null;
  const m = String(str).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) return new Date(parseInt(m[3]), parseInt(m[2]) - 1, parseInt(m[1]));
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
};

const SortTh = ({ label, field, sortField, sortDir, onSort, className = '' }) => (
  <th
    onClick={() => onSort(field)}
    className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider group cursor-pointer select-none whitespace-nowrap hover:bg-gray-100 ${className}`}
  >
    <span className="inline-flex items-center gap-1">
      {label}
      {sortField === field
        ? (sortDir === 'asc' ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />)
        : <ChevronsUpDown className="w-3.5 h-3.5 opacity-30 group-hover:opacity-60" />}
    </span>
  </th>
);

const Complaints = () => {
  const { hasPermission } = useAuth();
  const canExport = hasPermission('qm:view');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    partner: 'all',
    manager: 'all',
    calendarYear: 'all',
    searchQuery: '',
  });
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(1);

  useEffect(() => { fetchComplaintsData(); }, []);

  const fetchComplaintsData = async () => {
    try {
      setLoading(true);
      setError(null);
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${API_BASE_URL}/api/qm/registers/complaints/stats`, {
        method: 'GET',
        credentials: 'include',
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch complaints data');
      }
      const result = await response.json();
      setStats(result.data);
    } catch (err) {
      console.error('Error fetching complaints:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ISO 'YYYY-MM-DD' from the API → display DD-MM-YYYY (string swap, no TZ shift)
  const formatDate = (dateStr) => (dateStr ? toDisplayDate(dateStr) : 'N/A');

  // ── Filtered ──────────────────────────────────────────────────────────────
  const filteredComplaints = useMemo(() => {
    if (!stats?.complaints) return [];
    return stats.complaints.filter(c => {
      if (filters.status !== 'all' && (c.status || 'Unknown') !== filters.status) return false;
      // 人名是當時的快照——篩選與下拉一律認現名，否則改名過的人會變成兩個選項
      // （docs/data-model.md §2）。
      if (filters.partner !== 'all' && (staffNow(c.engagementPartner, c.engagementPartnerNow) || 'Unknown') !== filters.partner) return false;
      if (filters.manager !== 'all' && (staffNow(c.engagementManager, c.engagementManagerNow) || 'Unknown') !== filters.manager) return false;
      if (filters.calendarYear !== 'all') {
        const d = parseDMY(c.dateReceived);
        if ((d ? String(d.getFullYear()) : 'Unknown') !== filters.calendarYear) return false;
      }
      if (filters.searchQuery) {
        const q = filters.searchQuery.toLowerCase();
        const searchable = [c.clientName, c.natureOfComplaint, c.engagementPartner, c.engagementManager,
          c.engagementPartnerNow, c.engagementManagerNow]
          .map(v => (v || '').toLowerCase());
        if (!searchable.some(s => s.includes(q))) return false;
      }
      return true;
    });
  }, [stats, filters]);

  // ── Sorted ────────────────────────────────────────────────────────────────
  const sortedComplaints = useMemo(() => {
    if (!sortField) return filteredComplaints;
    return [...filteredComplaints].sort((a, b) => {
      const aVal = a[sortField] ?? '';
      const bVal = b[sortField] ?? '';
      const aDate = parseDMY(aVal);
      const bDate = parseDMY(bVal);
      if (aDate && bDate) return sortDir === 'asc' ? aDate - bDate : bDate - aDate;
      // numeric sort for id
      const aNum = Number(aVal);
      const bNum = Number(bVal);
      if (!isNaN(aNum) && !isNaN(bNum)) return sortDir === 'asc' ? aNum - bNum : bNum - aNum;
      const cmp = String(aVal).toLowerCase() < String(bVal).toLowerCase() ? -1
        : String(aVal).toLowerCase() > String(bVal).toLowerCase() ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filteredComplaints, sortField, sortDir]);

  const paginated = sortedComplaints.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
    setPage(1);
  };

  const setFilter = (key, value) => {
    setFilters(f => ({ ...f, [key]: value }));
    setPage(1);
  };

  // ── Dropdown options ──────────────────────────────────────────────────────
  const statusOptions = useMemo(() =>
    stats ? [...new Set(stats.complaints.map(c => c.status || 'Unknown'))].sort() : [], [stats]);
  const partnerOptions = useMemo(() =>
    stats ? [...new Set(stats.complaints.map(c => staffNow(c.engagementPartner, c.engagementPartnerNow) || 'Unknown'))].sort() : [], [stats]);
  const managerOptions = useMemo(() =>
    stats ? [...new Set(stats.complaints.map(c => staffNow(c.engagementManager, c.engagementManagerNow) || 'Unknown'))].sort() : [], [stats]);
  const calendarYearOptions = useMemo(() =>
    stats ? [...new Set(stats.complaints.map(c => {
      const d = parseDMY(c.dateReceived);
      return d ? String(d.getFullYear()) : null;
    }).filter(Boolean))].sort().reverse() : [], [stats]);

  // ── Loading / error states ────────────────────────────────────────────────
  if (loading) return <div className="flex justify-center items-center h-64"><div className="text-gray-600">Loading complaints data...</div></div>;
  if (error) return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
      <h3 className="text-red-800 font-semibold mb-2">Error Loading Complaints</h3>
      <p className="text-red-600">{error}</p>
      <button onClick={fetchComplaintsData} className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700">Retry</button>
    </div>
  );
  if (!stats) return <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4"><p className="text-yellow-800">No complaints data available.</p></div>;

  return (
    <div className="space-y-6">
      {/* Header — unified tab header (docs/dashboard-ui.md) */}
      <DashboardTabHeader
        description="Register of client complaints received and their resolution status"
        actions={canExport && (
          <button
            onClick={() => {
              const rows = sortedComplaints.map(c => ({
                'ID': c.id || '',
                'Date Received': formatDate(c.dateReceived),
                'Client / Engagement': c.clientName || '',
                'Financial Year': c.financialYear || '',
                'Engagement Partner': staffNow(c.engagementPartner, c.engagementPartnerNow) || '',
                'Engagement Manager/Director': staffNow(c.engagementManager, c.engagementManagerNow) || '',
                'Nature of Complaint': c.natureOfComplaint || '',
                'Status': c.status || '',
              }));
              exportToExcel(rows, 'Complaints_Report.xlsx');
            }}
            className={exportExcelBtnCls}
          >
            <Download className="w-4 h-4" />
            Export Excel
          </button>
        )}
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
          <div className="text-gray-600 text-sm">Total Complaints</div>
          <div className="text-3xl font-bold text-gray-900 mt-2">{stats.total}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
          <div className="text-gray-600 text-sm">Closed</div>
          <div className="text-3xl font-bold text-green-600 mt-2">{stats.resolved}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-yellow-500">
          <div className="text-gray-600 text-sm">Pending</div>
          <div className="text-3xl font-bold text-yellow-600 mt-2">{stats.pending}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-purple-500">
          <div className="text-gray-600 text-sm">Resolution Rate</div>
          <div className="text-3xl font-bold text-purple-600 mt-2">{stats.resolutionRate}%</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <input
              type="text"
              value={filters.searchQuery}
              onChange={(e) => setFilter('searchQuery', e.target.value)}
              placeholder="Client, nature, partner…"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select value={filters.status} onChange={(e) => setFilter('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
              <option value="all">All Statuses</option>
              {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Partner</label>
            <select value={filters.partner} onChange={(e) => setFilter('partner', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
              <option value="all">All Partners</option>
              {partnerOptions.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Manager/Director</label>
            <select value={filters.manager} onChange={(e) => setFilter('manager', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
              <option value="all">All Managers/Directors</option>
              {managerOptions.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Calendar Year</label>
            <select value={filters.calendarYear} onChange={(e) => setFilter('calendarYear', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
              <option value="all">All Years</option>
              {calendarYearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>
        <div className="mt-3 text-sm text-gray-600">
          Showing {sortedComplaints.length} of {stats.total} complaints
        </div>
      </div>

      {/* Complaints Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="table-scroll">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortTh label="ID"        field="id"                  sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortTh label="Date"      field="dateReceived"        sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortTh label="Client"    field="clientName"          sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortTh label="FY"        field="financialYear"       sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortTh label="Partner"   field="engagementPartner"   sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortTh label="Manager/Director" field="engagementManager"   sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortTh label="Nature"    field="natureOfComplaint"   sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortTh label="Status"    field="status"              sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginated.length === 0 ? (
                <tr><td colSpan="8" className="px-6 py-8 text-center text-gray-500">No complaints match the selected filters</td></tr>
              ) : (
                paginated.map((complaint, index) => (
                  <tr key={complaint.id || index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{complaint.id || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{complaint.dateReceived ? formatDateShort(complaint.dateReceived) : 'N/A'}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{complaint.clientName || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{complaint.financialYear || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      <NowName value={complaint.engagementPartner} now={complaint.engagementPartnerNow} fallback="N/A" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      <NowName value={complaint.engagementManager} now={complaint.engagementManagerNow} fallback="N/A" />
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 min-w-[260px] whitespace-pre-wrap break-words">{complaint.natureOfComplaint || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        complaint.status && complaint.status.toLowerCase().includes('closed')
                          ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>{complaint.status || 'Unknown'}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <TablePager
          page={page}
          setPage={setPage}
          total={sortedComplaints.length}
          className="px-6 py-3 flex items-center justify-between border-t border-gray-200 bg-gray-50"
        />
      </div>
    </div>
  );
};

export default Complaints;